import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiRefreshCw, FiX } from "react-icons/fi";
import { getCurrentHrmSession, getRoninGame } from "../api/hrmApi";
import HrmLogin from "./HrmLogin";
import HrmRoninLaunch from "./HrmRoninLaunch";
import { attachRoninCloudBridge } from './roninCloudBridge';
import "../styles/newHrm.css";
import "../styles/premiumHrm.css";
import "../styles/revampedHrm.css";
import "../styles/hrmRonin.css";

export const RONIN_READY_TIMEOUT_MS = 90000;
export const RONIN_SESSION_RECHECK_MS = 60000;

// Startup milestones, not a simulated download counter. Only verified readiness completes loading.
const LOAD_STAGES = {
  access: { percent: 0, label: "Checking secure access..." },
  metadata: { percent: 15, label: "Preparing your game..." },
  game: { percent: 30, label: "Loading Ronin..." },
  starting: { percent: 85, label: "Starting Ronin..." },
};

const HrmRoninPage = () => {
  const [playRequested, setPlayRequested] = useState(() => new URLSearchParams(window.location.search).get("play") === "1");
  const [frame, setFrame] = useState(null);
  const [ready, setReady] = useState(false);
  const [quarantined, setQuarantined] = useState(false);
  const [checking, setChecking] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState("");
  const [loadStage, setLoadStage] = useState("access");
  const iframeRef = useRef(null);
  const frameRef = useRef(null);
  const readyFrameRef = useRef(null);
  const requestRef = useRef(null);
  const retryRequiredRef = useRef(false);
  const frameSequence = useRef(0);

  const clearFrame = useCallback(() => {
    frameRef.current = null;
    readyFrameRef.current = null;
    setFrame(null);
    setReady(false);
    setQuarantined(false);
    setLoadStage("access");
  }, []);

  const expireSession = useCallback(() => {
    retryRequiredRef.current = false;
    requestRef.current?.abort();
    requestRef.current = null;
    clearFrame();
    setChecking(false);
    setError("");
    setNeedsLogin(true);
  }, [clearFrame]);

  const validateSession = useCallback(async ({ reload = false, failureMessage = "" } = {}) => {
    // Keep Retry stable when window focus returns during a click on the error screen.
    if (retryRequiredRef.current && !reload) return;
    if (reload) retryRequiredRef.current = false;
    if (requestRef.current && !reload && !failureMessage) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setChecking(true);
    // A focus check must not unmount Google's sign-in UI during authentication.
    if (reload) setNeedsLogin(false);
    setError("");
    if (failureMessage || (reload && (!frameRef.current || readyFrameRef.current !== frameRef.current))) {
      clearFrame();
    } else if (reload) {
      setQuarantined(true);
      setLoadStage("access");
    }

    try {
      const session = await getCurrentHrmSession({ signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!session?.user) {
        expireSession();
        return;
      }
      if (frameRef.current && frameRef.current.userId !== session.user.id) clearFrame();
      if (!frameRef.current) setLoadStage("metadata");
      const game = await getRoninGame({ signal: controller.signal });
      if (controller.signal.aborted) return;
      if (failureMessage) throw new Error(failureMessage);
      setNeedsLogin(false);

      const current = frameRef.current;
      const userId = session.user.id;
      // Keep the running game only when both verified identities and the build match.
      if (!current || current.playerKey !== game.playerKey || current.userId !== userId ||
          current.version !== game.version || current.pageUrl !== game.pageUrl) {
        const nextFrame = { ...game, userId, instance: ++frameSequence.current };
        frameRef.current = nextFrame;
        readyFrameRef.current = null;
        setReady(false);
        setLoadStage("game");
        setFrame(nextFrame);
      }
      setQuarantined(false);
    } catch (loadError) {
      if (controller.signal.aborted) return;
      if (loadError.status === 401) {
        expireSession();
        return;
      }
      // Retain session-only progress through transport/server failures, never denied access.
      const transient = !loadError.status || loadError.status === 408 || loadError.status === 429 || loadError.status >= 500;
      if (transient && frameRef.current && readyFrameRef.current === frameRef.current) {
        iframeRef.current?.blur();
        setQuarantined(true);
      } else {
        clearFrame();
      }
      retryRequiredRef.current = true;
      setError(loadError.message || "Ronin could not be loaded. Please retry.");
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setChecking(false);
      }
    }
  }, [clearFrame, expireSession]);

  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== "hidden") void validateSession();
    };
    const onStorage = (event) => {
      if (!event.key || (["newHrmSession", "awcUser"].includes(event.key) && event.newValue === null)) {
        expireSession();
      } else if (["newHrmSession", "awcUser"].includes(event.key)) {
        revalidate();
      }
    };
    const onPageShow = (event) => {
      if (event.persisted) revalidate();
    };
    const onReady = (event) => {
      const current = frameRef.current;
      if (!current || !iframeRef.current || event.source !== iframeRef.current.contentWindow ||
          event.origin !== new URL(current.pageUrl, window.location.href).origin ||
          event.data?.type !== "antrazal:ronin:ready" ||
          event.data.version !== current.version || event.data.playerKey !== current.playerKey) return;
      setReady(true);
      readyFrameRef.current = current;
    };

    window.addEventListener("focus", revalidate);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    window.addEventListener("message", onReady);
    window.addEventListener("awc-auth-expired", expireSession);
    document.addEventListener("visibilitychange", revalidate);
    void validateSession();
    return () => {
      requestRef.current?.abort();
      requestRef.current = null;
      frameRef.current = null;
      readyFrameRef.current = null;
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("message", onReady);
      window.removeEventListener("awc-auth-expired", expireSession);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [expireSession, validateSession]);

  useEffect(() => {
    if (!playRequested || !frame || quarantined || needsLogin) return undefined;
    return attachRoninCloudBridge({ frame, iframe: iframeRef.current, onExpired: expireSession,
      isActive: () => frameRef.current === frame });
  }, [playRequested, frame, quarantined, needsLogin, expireSession]);

  useEffect(() => {
    if (!playRequested || !frame || ready) return undefined;
    const timeout = window.setTimeout(() => {
      // Recheck the cookie after a failed frame load so an expired login is not a server error.
      void validateSession({ failureMessage: "Ronin did not finish loading. Please retry." });
    }, RONIN_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [playRequested, frame, ready, validateSession]);

  useEffect(() => {
    if (!frame || quarantined || (playRequested && !ready)) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void validateSession();
    }, RONIN_SESSION_RECHECK_MS);
    return () => window.clearInterval(interval);
  }, [playRequested, frame, ready, quarantined, validateSession]);

  const showGame = Boolean(playRequested && frame && ready && !quarantined && !error && !needsLogin);
  const loading = LOAD_STAGES[loadStage];
  const playGame = () => {
    setPlayRequested(true);
    void validateSession({ reload: true });
  };

  const cancelLoad = () => {
    retryRequiredRef.current = false;
    requestRef.current?.abort();
    requestRef.current = null;
    clearFrame();
    setChecking(false);
    setError("");
    setPlayRequested(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("play");
    window.history.replaceState(null, "", url.pathname + url.search);
  };
  return (
    <div className="hrm-ronin-page new-hrm-premium-root new-hrm-app--premium">
      {playRequested && frame && (
        <iframe
          key={frame.instance}
          ref={iframeRef}
          className="hrm-ronin-page__frame"
          title="Ronin"
          src={frame.pageUrl}
          sandbox="allow-scripts allow-same-origin allow-downloads allow-popups allow-popups-to-escape-sandbox"
          allow="fullscreen; autoplay"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          aria-hidden={!showGame}
          aria-busy={checking}
          // Background rechecks must not blur or disable an already verified game.
          inert={!showGame ? "" : undefined}
          tabIndex={showGame ? 0 : -1}
          style={{ visibility: showGame ? "visible" : "hidden" }}
          onLoad={(event) => {
            if (frameRef.current?.instance !== frame.instance) return;
            setLoadStage("starting");
            event.currentTarget.contentWindow?.postMessage(
              { type: "antrazal:ronin:probe" },
              new URL(frame.pageUrl, window.location.href).origin,
            );
          }}
          onErrorCapture={() => void validateSession({ failureMessage: "Ronin could not be loaded. Please retry." })}
        />
      )}
      {needsLogin ? (
        <section className="hrm-ronin-page__status hrm-ronin-page__signin" aria-label="Ronin sign-in">
          <img className="hrm-ronin-page__artwork" src="/assets/ronin/village-art.webp" alt="" width="1024" height="1010" />
          <img className="hrm-ronin-page__brand" src="/assets/ronin/antrazal-white-r2.svg" alt="Antrazal" width="2048" height="609" />
          <HrmLogin onLogin={() => void validateSession({ reload: true })} />
          <a className="hrm-ronin-page__back" href="/HRM"><FiArrowLeft aria-hidden="true" />HRM</a>
        </section>
      ) : !playRequested ? (
        <HrmRoninLaunch
          checking={checking}
          error={error}
          onPlay={playGame}
          onRetry={() => void validateSession({ reload: true })}
        />
      ) : !showGame && (
        <main className="hrm-ronin-page__status">
          <img className="hrm-ronin-page__artwork" src="/assets/ronin/village-art.webp" alt="" width="1024" height="1010" />
          <img className="hrm-ronin-page__brand" src="/assets/ronin/antrazal-white-r2.svg" alt="Antrazal" width="2048" height="609" />
          <div className="hrm-ronin-page__loading-content">
            <h1>Ronin</h1>
            {error ? <p className="hrm-ronin-page__error" role="alert">{error}</p> : (
              <div className="hrm-ronin-page__progress">
                <div className="hrm-ronin-page__progress-label">
                  <p id="ronin-loading-status" role="status">{loading.label}</p>
                  <span aria-hidden="true">{loading.percent}%</span>
                </div>
                <div
                  className="hrm-ronin-page__progress-track"
                  role="progressbar"
                  aria-label="Ronin loading progress"
                  aria-describedby="ronin-loading-status"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={loading.percent}
                  aria-valuetext={`${loading.percent}% - ${loading.label}`}
                >
                  <span style={{ width: `${loading.percent}%` }} />
                </div>
              </div>
            )}
            <div className="hrm-ronin-page__actions">
              {error ? (
                <button key="retry" type="button" onClick={() => void validateSession({ reload: true })}>
                  <FiRefreshCw aria-hidden="true" />Retry
                </button>
              ) : (
                <button key="cancel" type="button" className="hrm-ronin-page__cancel" onClick={cancelLoad}>
                  <FiX aria-hidden="true" />Cancel
                </button>
              )}
              <a href="/HRM"><FiArrowLeft aria-hidden="true" />HRM</a>
            </div>
          </div>
        </main>
      )}
    </div>
  );
};

export default HrmRoninPage;
